import React, { useState, useMemo } from 'react';
import { MedicareBaseRateType } from '../../../../services/ConfigurationService/types/centerType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface MedicareBaseRateGridProps {
  medicareRates: MedicareBaseRateType[];
  onRowSelect?: (rate: MedicareBaseRateType) => void;
}

interface MedicareBaseRateGridRow {
  id: string;
  year: number;
  cbsa: string;
  wageIndex: number | null;
  lrop: number | null;
  nlop: number | null;
  baseRate: number;
  federalCapitalWeight: number | null;
  geographicAdjustmentFactor: number | null;
  inpatientHospitalDeductible: number | null;
}

export function MedicareBaseRateGrid({ medicareRates, onRowSelect }: MedicareBaseRateGridProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter medicare rates based on search term
  const filteredRates = useMemo(() => {
    if (!searchTerm.trim()) {
      return medicareRates;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return medicareRates.filter(rate => 
      rate.year.toString().includes(searchLower) ||
      rate.cbsa.toLowerCase().includes(searchLower) ||
      (rate.baseRate?.toString().includes(searchLower) ?? false) ||
      (rate.wageIndex?.toString().includes(searchLower) ?? false) ||
      (rate.lrop?.toString().includes(searchLower) ?? false) ||
      (rate.nlop?.toString().includes(searchLower) ?? false) ||
      (rate.federalCapitalWeight?.toString().includes(searchLower) ?? false) ||
      (rate.geographicAdjustmentFactor?.toString().includes(searchLower) ?? false) ||
      (rate.inpatientHospitalDeductible?.toString().includes(searchLower) ?? false)
    );
  }, [medicareRates, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data && onRowSelect) {
      const selectedRow = args.data as MedicareBaseRateGridRow;
      const rate = medicareRates.find(r => r.id === selectedRow.id);
      if (rate) {
        onRowSelect(rate);
      }
    }
  };

  if (medicareRates.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by year or rate..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No Medicare base rates found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No Medicare base rates configured for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredRates.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by year or rate..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No Medicare base rates match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search Medicare base rates..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredRates}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="year" 
            headerText="Year" 
            width="80" 
            textAlign="Right" 
            type="number"
          />
          <ColumnDirective 
            field="cbsa" 
            headerText="CBSA" 
            width="120" 
            textAlign="Left"
          />
          <ColumnDirective 
            field="wageIndex" 
            headerText="Wage Index" 
            width="110" 
            textAlign="Right" 
            type="number"
            format="N4"
          />
          <ColumnDirective 
            field="lrop" 
            headerText="LROP" 
            width="100" 
            textAlign="Right" 
            type="number"
            format="C2"
          />
          <ColumnDirective 
            field="nlop" 
            headerText="NLOP" 
            width="100" 
            textAlign="Right" 
            type="number"
            format="C2"
          />
          <ColumnDirective 
            field="baseRate" 
            headerText="Base Rate" 
            width="110" 
            textAlign="Right" 
            type="number"
            format="C2"
          />
          <ColumnDirective 
            field="federalCapitalWeight" 
            headerText="Fed Capital Weight" 
            width="130" 
            textAlign="Right" 
            type="number"
            format="N4"
          />
          <ColumnDirective 
            field="geographicAdjustmentFactor" 
            headerText="Geo Adjust Factor" 
            width="130" 
            textAlign="Right" 
            type="number"
            format="N4"
          />
          <ColumnDirective 
            field="inpatientHospitalDeductible" 
            headerText="Inpatient Deductible" 
            width="140" 
            textAlign="Right" 
            type="number"
            format="C2"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
}